import { BellAlertIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { RealTimeWrapper } from "components";
import { useUnreadNotificationMessages } from "hooks/data/use-unread-notification-messages";
import React, { Fragment, useMemo } from "react";
import { Link } from "react-location";

type Props = {
  isCollapsed: boolean;
};

const PlaceItNotifications = ({ isCollapsed }: Props) => {
  const { unreadNotificationMessages, refetch } =
    useUnreadNotificationMessages();

  const getTotalThreadNotifications = useMemo(() => {
    return unreadNotificationMessages.reduce((total, notification) => {
      return total + (notification?.thread_notification?.length || 0);
    }, 0);
  }, [unreadNotificationMessages]);

  return (
    <RealTimeWrapper
      channel="placeit-notification"
      callBack={(data) => {
        console.log("PlaceIT Notifications updated", data);
        refetch();
      }}
      events={[
        "cedant.manager_onboarded",
        "cedant.placed_risk",
        "cedant.updated_risk",
        "cedant.uploaded_supporting_documents",
        "cedant.send_thread_notification",
        "broker.send_thread_notification",
        "broker.update_offer",
        "broker.accept_offer",
        "broker.declined_offer",
      ]}
    >
      <Fragment>
        <Link
          to={"/place-it-offers"}
          className={clsx(
            "group flex items-center w-full px-2 py-2 text-sm rounded-md text-gray-300 hover:bg-sidebar-800 hover:text-white relative", // Added w-full for consistent layout
            isCollapsed && "justify-center"
          )}
          title="PlaceIT Notifications"
        >
          {/* Icon and Badge Container */}
          <div className="relative flex-shrink-0">
            <BellAlertIcon
              className={clsx(
                "text-gray-200 group-hover:text-gray-50 h-6 w-6"
                // Margin is handled by the text span container below when expanded
              )}
              aria-hidden="true"
            />
            {/* Badge for Collapsed State (positioned relative to icon) */}
            {isCollapsed && getTotalThreadNotifications > 0 && (
              <span
                className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full transform translate-x-1/4 -translate-y-1/4"
                aria-label={`${getTotalThreadNotifications} PlaceIT notifications`}
              >
                {/* Optionally cap the displayed count */}
                {getTotalThreadNotifications > 9
                  ? "9+"
                  : getTotalThreadNotifications}
              </span>
            )}
          </div>

          {/* Text and Badge for Expanded State */}
          {!isCollapsed && (
            <span className="flex-1 flex justify-between items-center ml-3">
              {" "}
              {/* Added ml-3 for spacing from icon */}
              <span>PlaceIT Notifications</span>
              {/* Badge for Expanded State (aligned to the right) */}
              {getTotalThreadNotifications > 0 && (
                <span
                  className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full"
                  aria-label={`${getTotalThreadNotifications} PlaceIT notifications`}
                >
                  {getTotalThreadNotifications}
                </span>
              )}
            </span>
          )}

          {/* Tooltip for Collapsed State */}
          {isCollapsed && (
            <span className="absolute left-full ml-2 w-auto min-w-max rounded bg-black text-xs text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
              PlaceIT Notifications
            </span>
          )}
        </Link>
      </Fragment>
    </RealTimeWrapper>
  );
};

export default PlaceItNotifications;
